import React from 'react';
import dayjs from 'dayjs';
import { IonContent, IonLabel, IonSegmentButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { Modal } from '../../components/Modal';
import { CheckinState } from '../model/CheckinState';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { useBreakpoints } from '../../theme/useBreakpoints';
import { PersonalCheckinEditor } from './PersonalCheckinEditor';
import { CareCheckinEditor } from './CareCheckinEditor';
import { IOSTabs } from '../../components/IOSTabs';
import { ICareEvent, IFeeling, PERSONAL_TIME_LABELS } from '../model/ICheckin';

export const EditCheckinModal: React.FC = () => {
    const history = useInstance(HistoryViewModel);

    const checkinState = useInstance(CheckinState);
    useSubscription(() => checkinState);

    const mixpanel = useInstance(MixPanelEventEmitter);

    const { isBelowBreakpoint } = useBreakpoints();

    const checkin = checkinState.getEditingCheckin('personal');

    const checkinDate = checkin ? checkinState.getCheckinDate(checkin) : undefined;
    const isToday = !checkinDate || dayjs(checkinDate).isToday();

    const titleDateFormat = isBelowBreakpoint('md') ? `${isToday ? '' : 'ddd,'} MMM D` : `${isToday ? '' : 'dddd,'} MMMM D`;
    const title = (isToday ? 'Today, ' : '') + dayjs(checkinDate).format(titleDateFormat);

    const doneEditing = () => {
        const personal = checkinState.getEditingCheckin('personal')!;
        const care = checkinState.getEditingCheckin('care');

        /*
        Care = selection in care event, and/or care note content
        Personal = selection in sentiment (smileys), emotions, life balance, and/or personal note content
        Personal + Care = at least one from Personal and one from Care above
        None = if no field was populated (essentially a user opening the daily log and closing it so we can ideally filter out some of the false positives with autosave)
        */
        const hasCare = (care?.careEvents?.length || care?.note.length);
        const hasPersonal = (personal.feeling || personal.emotions?.length || personal.lifeEvents?.length || personal.note.length);
        const contentType = hasPersonal && hasCare ? 'Personal + Care' : hasPersonal ? 'Personal' : hasCare ? 'Care' : 'None';

        // get event hours, or undefined if zero (this is what we want to send to mixpanel)
        const getEventHours = (event: ICareEvent) => care?.careEventHours?.[event] || undefined;

        mixpanel.track('Daily Log Completed', () => ({
            'From': checkinState.state.trackingFrom || 'caregiver corner',
            'Day': mixpanel.createMixpanelDateWithoutTime(checkinState.getCheckinDate(personal)),
            'Entered For': isToday ? 'Today' : 'Past',
            'Emotions': personal.emotions?.length || 0,
            'Life Balance': personal.lifeEvents?.length || 0,
            'Care Events': care?.careEvents?.length  || 0,
            'Personal Note Length': personal.note.length,
            'Care Note Length': care?.note.length || 0,
            'Personal Attachments': personal.attachments.length,
            'Care Attachments': care?.attachments.length || 0,
            'Content Type': contentType, // Personal | Care | Personal + Care | None
            'Daily Sentiment': IFeeling[personal.feeling!]?.split(/(?=[A-Z])/).join(' '), //Very Good | Good | Neutral | Bad | Very Bad
            'Energy': personal.energy,
            'Total Care Hours': care?.totalCareHours,

            'Personal Care Hours': getEventHours('personal care'),
            'Meal Prep Hours': getEventHours('meal prep'),
            'Appointment Hours': getEventHours('appointment'),
            'Meeting Hours': getEventHours('meeting'),
            'Achievement Hours': getEventHours('achievement'),
            'Medication Hours': getEventHours('medication'),
            'Exercise Hours': getEventHours('exercise'),
            'Social Activity Hours': getEventHours('social activity'),
            'Cleaning Hours': getEventHours('cleaning'),
            'Grocery Hours': getEventHours('groceries'),
            'Transport Hours': getEventHours('transport'),
            'Incident Hours': getEventHours('incident'),
            'Home Visit Hours': getEventHours('home visit'),
            'Respite Care Hours': getEventHours('respite care'),
            'New Routine Hours': getEventHours('new routine'),
            'Emergency Hours': getEventHours('emergency'),

            'Personal Time': personal.personalTime ? PERSONAL_TIME_LABELS[personal.personalTime] : undefined,
            'Miles Driven': care?.milesDriven
        }));

        checkinState.stopEditing();

        if (history.getCurrentLocation().pathname.includes('/daily-log')) {
            history.replace('/calendar/journal');
        }
    };

    return (
        <Modal
            centerTitle
            title={title}
            desktopMinHeight={590}
            closeHandler={doneEditing}
            data-id="edit-checkin-modal"
            isOpen={checkinState.isEditing()}
        >
            <IonContent>

                <div style={{display: 'flex', flexDirection: 'column', gap: 10, margin: '0 10px 20px'}}>

                    <div style={{marginLeft: 8, marginRight: 8}}>
                        <IOSTabs value={checkinState.state.checkinType} onChange={checkinType => checkinState.setState({checkinType: checkinType})}>

                            <IonSegmentButton value="personal">
                                <IonLabel>Personal</IonLabel>
                            </IonSegmentButton>

                            <IonSegmentButton value="care">
                                <IonLabel>Care</IonLabel>
                            </IonSegmentButton>

                        </IOSTabs>
                    </div>

                    {checkinState.state.checkinType === 'personal' &&
                        <PersonalCheckinEditor />
                    }

                    {checkinState.state.checkinType === 'care' &&
                        <CareCheckinEditor />
                    }
                </div>

            </IonContent>
        </Modal>
    );
};
